import { useLocation, useNavigate } from 'react-router-dom';
import { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown, ChevronUp, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import LocationMatch from '@/utils/locationMatch';
import { cn } from '@/lib/utils';

function Drawer({ menuItems, to, parent, disabledPath = false }) {
  const navigate = useNavigate();
  const loc = useLocation();
  const [openMenu, setOpenMenu] = useState(true);
  const [width, setWidth] = useState(window.innerWidth);
  const [openSubmenus, setOpenSubmenus] = useState({});

  const updateDimensions = () => {
    setWidth(window.innerWidth);
  };

  const toggleSubmenu = index => {
    setOpenSubmenus(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const MenuBar = menuItems.map((item, index) => {
    if (item.label === disabledPath) return null;

    const match = LocationMatch(`/${parent}/${item.label}`);
    const isSubmenuOpen = openSubmenus[index];

    if (item.label === 'wrapper' && item.children) {
      return (
        <Fragment key={index}>
          <Button
            variant="ghost"
            onClick={() => toggleSubmenu(index)}
            className={cn(
              'w-full justify-start gap-3 px-3 h-11 text-sm font-normal',
              'hover:bg-accent transition-colors',
            )}
          >
            {item.icon && (
              <span className="h-5 w-5 flex-shrink-0">{item.icon}</span>
            )}
            <span className="flex-1 text-left">{item.menu}</span>
            {isSubmenuOpen ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
          {isSubmenuOpen && (
            <div className="ml-4 space-y-1">
              {item.children.map((child, idx) => {
                const childMatch = LocationMatch(`/${parent}/${child.label}`);
                return (
                  <Button
                    key={idx}
                    variant="ghost"
                    onClick={() => navigate(`/${parent}/${child.label}`)}
                    className={cn(
                      'w-full justify-start gap-3 px-3 h-10 text-sm font-normal',
                      'hover:bg-accent transition-colors',
                      childMatch &&
                        'bg-orange-50 text-orange-700 hover:bg-orange-100 border-l-2 border-orange-500',
                    )}
                  >
                    {child.icon && (
                      <span className="h-4 w-4 flex-shrink-0">
                        {child.icon}
                      </span>
                    )}
                    <span className="flex-1 text-left">{child.menu}</span>
                  </Button>
                );
              })}
            </div>
          )}
        </Fragment>
      );
    }

    return (
      <Button
        key={index}
        variant="ghost"
        onClick={() => navigate(`/${parent}/${item.label}`)}
        className={cn(
          'w-full justify-start gap-3 px-3 h-11 text-sm font-normal',
          'hover:bg-accent transition-colors relative',
          match &&
            'bg-orange-50 text-orange-700 hover:bg-orange-100 border-l-2 border-orange-500',
        )}
      >
        {item.icon && (
          <span className="h-5 w-5 flex-shrink-0">{item.icon}</span>
        )}
        <span className="flex-1 text-left">{item.menu}</span>
        {item.label === 'support' && (
          <Badge variant="warning" className="text-[10px] px-1.5 py-0">
            BETA
          </Badge>
        )}
      </Button>
    );
  });

  const openSidebar = () => {
    if (!openMenu) {
      setOpenMenu(true);
    }
  };
  const closeSidebar = () => {
    if (openMenu) {
      setOpenMenu(false);
    }
  };

  useEffect(() => {
    window.addEventListener('resize', updateDimensions);
    if (width >= 1064) {
      setOpenMenu(true);
    } else {
      setOpenMenu(false);
    }
    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, [width]);

  useEffect(() => {
    if (width < 1064) {
      setTimeout(() => {
        setOpenMenu(false);
      }, 500);
    }
  }, [loc.pathname]);

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-4 left-4 z-50 lg:hidden"
        onClick={openSidebar}
      >
        <Menu className="h-5 w-5" />
      </Button>

      {/* Backdrop overlay for mobile */}
      {openMenu && width < 1064 && (
        <button
          type="button"
          className="fixed inset-0 bg-black/50 z-40 lg:hidden border-none p-0 cursor-default"
          onClick={closeSidebar}
          aria-label="Close sidebar"
        />
      )}

      <aside
        className={cn(
          'fixed left-0 top-0 z-50 h-full w-[280px] border-r bg-background transition-transform duration-300 lg:translate-x-0',
          !openMenu && width < 1064 && '-translate-x-full',
        )}
      >
        <div className="flex h-full flex-col">
          {/* Close button for mobile */}
          <div className="flex h-16 items-center justify-between border-b px-4">
            <button
              type="button"
              className="flex items-center gap-2 cursor-pointer bg-transparent border-none p-0"
              onClick={() => to && navigate(to)}
            >
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-orange-400 to-orange-600" />
              <span className="font-semibold text-lg">EximERP</span>
            </button>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={closeSidebar}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation menu */}
          <nav className="flex-1 overflow-y-auto px-3 py-4">
            <div className="space-y-1">{MenuBar}</div>
          </nav>
        </div>
      </aside>
    </>
  );
}

Drawer.propTypes = {
  menuItems: PropTypes.array.isRequired,
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  parent: PropTypes.string.isRequired,
  disabledPath: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
};

export default Drawer;
